import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet, GlassPill, Button } from './primitives';
import { useTeamSessions, type SessionsRow } from '../lib/queries/useTeamSessions';
import { useActiveTeam } from '../lib/queries/useActiveTeam';
import './NowSurface.css';

/**
 * "What's ready right now" panel — the brief's Layer-2 anchor.
 *
 * Web1's full NowSurface (apps/web/src/components/NowSurface/) was 330
 * lines of tight CoachShell coupling and a heavy `NowContext` build
 * pipeline that pulls sessions / tasks / video / curriculum state. Web2
 * deliberately ports a leaner version: render a sliding right sheet
 * with the next session, next game, and any draft sessions awaiting
 * planning — derived from existing web2 queries, no new pipeline.
 *
 * Future expansion (when richer ready items justify it): wire into
 * `@4cm/brain/nowContext` `buildNowContext()` once a web2 query layer
 * for it lands. Today's items keep the existing NowKind shape.
 */
interface NowSurfaceProps {
  open: boolean;
  onClose: () => void;
}

interface NowItem {
  id: string;
  title: string;
  context: string;
  accent: 'accent' | 'amber' | 'red' | 'neutral';
  cta: { label: string; route: string };
}

export function NowSurface({ open, onClose }: NowSurfaceProps) {
  const { team, loading: teamLoading } = useActiveTeam();
  const { rows } = useTeamSessions(team?.id, teamLoading, { order: 'asc', limit: 30 });
  const navigate = useNavigate();

  const items: NowItem[] = useMemo(() => {
    const now = new Date();
    const out: NowItem[] = [];

    const upcoming = rows
      .filter((s: SessionsRow) => s.date && new Date(s.date).getTime() >= now.getTime() - 6 * 60 * 60 * 1000)
      .sort((a: SessionsRow, b: SessionsRow) => new Date(a.date).getTime() - new Date(b.date).getTime());

    if (upcoming.length > 0) {
      const next = upcoming[0];
      const date = new Date(next.date);
      const hoursUntil = Math.max(0, Math.round((date.getTime() - now.getTime()) / (60 * 60 * 1000)));
      out.push({
        id: next.id,
        title: next.title ?? 'Next session',
        context: hoursUntil < 24 ? `In ${hoursUntil}h · ${date.toLocaleDateString()}` : date.toLocaleDateString(),
        accent: hoursUntil < 6 ? 'accent' : 'neutral',
        cta: { label: 'Open session', route: `/sessions/${next.id}` },
      });
    }

    const drillsMissing = rows.filter(
      (s: SessionsRow) => s.date && new Date(s.date).getTime() > now.getTime() && (!s.duration_mins || s.duration_mins < 30),
    );
    if (drillsMissing.length > 0) {
      out.push({
        id: 'plan-needed',
        title: `${drillsMissing.length} session${drillsMissing.length === 1 ? '' : 's'} need planning`,
        context: 'Drills not yet attached this week',
        accent: 'amber',
        cta: { label: 'Open this week', route: '/calendar' },
      });
    }

    if (out.length === 0) {
      out.push({
        id: 'empty',
        title: 'All caught up',
        context: 'No imminent sessions or planning gaps',
        accent: 'neutral',
        cta: { label: 'Plan a session', route: '/sessions/new' },
      });
    }

    return out;
  }, [rows]);

  if (!open) return null;

  const handleCta = (route: string) => {
    onClose();
    navigate(route);
  };

  return (
    <>
      <button
        type="button"
        className="now-scrim"
        aria-label="Close Today panel"
        onClick={onClose}
      />
      <Sheet side="right" label="Today — what's ready" className="now-sheet">
        <header className="now-sheet__head">
          <div>
            <span className="page-eyebrow">Today</span>
            <h2 className="now-sheet__title">What's ready</h2>
          </div>
          <button type="button" className="now-sheet__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <ul className="now-sheet__items">
          {items.map((item) => (
            <li key={item.id} className="now-item" data-accent={item.accent}>
              <div className="now-item__body">
                <h3 className="now-item__title">{item.title}</h3>
                <p className="now-item__context">{item.context}</p>
              </div>
              <Button
                variant="glass"
                size="touchline"
                onClick={() => handleCta(item.cta.route)}
              >
                {item.cta.label}
              </Button>
            </li>
          ))}
        </ul>
        <footer className="now-sheet__foot">
          <GlassPill mono>{items.length === 1 && items[0].id === 'empty' ? 'no pending' : `${items.length} ready`}</GlassPill>
        </footer>
      </Sheet>
    </>
  );
}
